import { makeAutoObservable } from "mobx";
import { groupBy, isObject, sortBy, sumBy, uniq, uniqBy } from "lodash";
import { DecisionType } from "@/common/decisions";
import { LocalDateFieldModel, NumberFieldModel } from "@/common/commonForms";
import { exercisableOn } from "@/instruments/optionsDomain";
import {
  HolderHoldingCompanies,
  instrumentHolderApiClient,
} from "@/instruments/instrumentsApiClient";
import { thunk } from "@/events/eventsDomain";
import { today, toLocalDateISOString } from "@/utils/Dates";
import { ShareType, ShareTypeCode } from "@/common/share";
import {
  DbotEventDispatcher,
  DecisionContext,
  DecisionItem,
  ExerciseCandidate,
} from "@/dbot/common/commonDomain";
import { TMessage } from "@/i18n/tt";
import { TMessageKey } from "@/i18n/types.gen";

export type ProgramExercise = {
  grantId: string;
  trancheId: string;
  holderId: string;
  holderName: string;
  shareholderId: number;
  shareTypeId: number;
  shareTypeCode: ShareTypeCode;
  strikePrice: number;
  count: number;
  isEmployee: boolean;
  receiver?: HoldingCompanyReceiver;
};

export type HoldingCompanyReceiver = {
  organisationNumber: string;
  name: string;
  shareholderId?: number;
  contactEmail?: string;
};

export type ReceiverChoice = {
  name: string;
  organisationNumber?: string;
  receiver?: HoldingCompanyReceiver;
};

export type ExerciseProgram = {
  planId: string;
  programName: string;
  exercises: ProgramExercise[];
};

export type OptionExercise = {
  type: DecisionType.optionExercise;
  programs: ExerciseProgram[];
  capitalIncreaseDecisionId?: string;
  exerciseDate?: string;
};

export type ExerciseProgramChoice = {
  planId: string;
  programName: string;
};

export const exerciseProgramChoices = (candidates: ExerciseCandidate[]): ExerciseProgramChoice[] =>
  sortBy(
    uniqBy(candidates, (candidate) => candidate.planId).map(({ planId, programName }) => ({
      planId,
      programName,
    })),
    (choice) => choice.programName,
  );

export const exerciseBlocker = (candidate: ExerciseCandidate): TMessage | undefined => {
  if (candidate.shareholderId === undefined) {
    return { key: "decisions.optionExercise.no-shareholder" };
  }
  return undefined;
};

export type ExerciseSubscription = {
  subscriberName: string;
  shareTypeCode: ShareTypeCode;
  numberOfShares: number;
  price: number;
};

export const exerciseSubscriptions = (exercise: OptionExercise): ExerciseSubscription[] =>
  Object.values(
    groupBy(
      exercise.programs.flatMap((program) => program.exercises),
      (e) =>
        `${e.receiver?.organisationNumber ?? e.shareholderId}/${e.shareTypeId}/${e.strikePrice}`,
    ),
  ).map((exercises) => ({
    subscriberName: exercises[0].receiver?.name ?? exercises[0].holderName,
    shareTypeCode: exercises[0].shareTypeCode,
    numberOfShares: sumBy(exercises, (e) => e.count),
    price: exercises[0].strikePrice,
  }));

export type LinkedOptionExercise = {
  decisionId: string;
  exercise: OptionExercise;
};

export const findLinkedOptionExercise = (
  decisions: DecisionItem[],
  capitalIncreaseDecisionId: string,
): LinkedOptionExercise | undefined =>
  decisions.flatMap(({ decisionId, decisionData }) =>
    decisionData?.type === DecisionType.optionExercise &&
    decisionData.capitalIncreaseDecisionId === capitalIncreaseDecisionId
      ? [{ decisionId, exercise: decisionData }]
      : [],
  )[0];

const knownApplyErrors: Partial<Record<string, TMessageKey>> = {
  ExerciseSharesMismatch: "decisions.optionExercise.apply-mismatch",
  ExerciseAboveExercisable: "options.exercise-error-above-exercisable",
  NotHoldingCompany: "options.exercise-error-not-holding-company",
};

export const exerciseApplyErrorMessage = (data: unknown): TMessage | undefined => {
  if (!isObject(data) || !("error" in data) || typeof data.error !== "string") {
    return undefined;
  }
  const key = knownApplyErrors[data.error];
  if (key !== undefined) {
    return { key };
  }
  return "message" in data && typeof data.message === "string"
    ? { translated: data.message }
    : undefined;
};

const savedCandidate = (
  program: ExerciseProgram,
  exercise: ProgramExercise,
): ExerciseCandidate => ({
  ...exercise,
  planId: program.planId,
  programName: program.programName,
  exercisable: 0,
});

const candidatesIn = (context: DecisionContext<OptionExercise>): ExerciseCandidate[] =>
  context.contextData[context.orgNo]?.optionExercises ?? [];

const shareTypesIn = (context: DecisionContext<OptionExercise>): ShareType[] =>
  (context.contextData[context.orgNo]?.initialState.shareTypes ?? []).filter(
    (shareType) => !shareType.historical,
  );

const onlyShareType = (shareTypes: ShareType[]): ShareType | undefined =>
  shareTypes.length === 1 ? shareTypes[0] : undefined;

type RowShareType = {
  shareTypeId: number;
  shareTypeCode: ShareTypeCode;
};

const rowShareType = (
  candidate: ExerciseCandidate,
  saved: ProgramExercise | undefined,
  defaultShareType: ShareType | undefined,
): RowShareType | undefined => {
  if (saved !== undefined) {
    return { shareTypeId: saved.shareTypeId, shareTypeCode: saved.shareTypeCode };
  }
  if (candidate.shareTypeId !== undefined && candidate.shareTypeCode !== undefined) {
    return { shareTypeId: candidate.shareTypeId, shareTypeCode: candidate.shareTypeCode };
  }
  return (
    defaultShareType && {
      shareTypeId: defaultShareType.sharetypeId,
      shareTypeCode: defaultShareType.shareTypeCode,
    }
  );
};

const exercisableFor = (candidate: ExerciseCandidate, date: string | undefined): number =>
  candidate.tranche !== undefined && date !== undefined
    ? exercisableOn(candidate.tranche, date)
    : 0;

export class ExerciseRowModel {
  readonly candidate: ExerciseCandidate;
  readonly count: NumberFieldModel;
  private readonly exerciseDate: () => string | undefined;
  isSelected: boolean;
  shareType?: RowShareType;
  receiver?: HoldingCompanyReceiver;
  isShareTypeMissing = false;

  constructor(
    candidate: ExerciseCandidate,
    saved: ProgramExercise | undefined,
    selectByDefault: boolean,
    exerciseDate: () => string | undefined,
    defaultShareType?: ShareType,
  ) {
    this.candidate = candidate;
    this.exerciseDate = exerciseDate;
    this.shareType = rowShareType(candidate, saved, defaultShareType);
    this.receiver = saved?.receiver;
    const exercisable = exercisableFor(candidate, exerciseDate());
    this.isSelected =
      saved !== undefined ||
      (selectByDefault && exerciseBlocker(candidate) === undefined && exercisable > 0);
    this.count = new NumberFieldModel({
      value: saved?.count ?? exercisable,
      required: true,
      isInteger: true,
      isPositive: true,
      validator: (value) =>
        value !== undefined && value > this.exercisable
          ? {
              key: "decisions.optionExercise.above-exercisable",
              params: { count: { type: "number", value: this.exercisable } },
            }
          : undefined,
    });
    makeAutoObservable<ExerciseRowModel, "exerciseDate">(
      this,
      { candidate: false, exerciseDate: false },
      { autoBind: true },
    );
  }

  get exercisable(): number {
    return exercisableFor(this.candidate, this.exerciseDate());
  }

  get blocker(): TMessage | undefined {
    return exerciseBlocker(this.candidate);
  }

  get needsShareClass(): boolean {
    return this.candidate.shareTypeId === undefined;
  }

  toggle() {
    this.isSelected = !this.isSelected;
  }

  setReceiver(receiver: HoldingCompanyReceiver | undefined) {
    this.receiver = receiver;
  }

  setShareType(shareType: ShareType) {
    this.shareType = { shareTypeId: shareType.sharetypeId, shareTypeCode: shareType.shareTypeCode };
    this.isShareTypeMissing = false;
  }

  get isValid(): boolean {
    const count = this.count.parsedValue;
    return (
      !this.isSelected ||
      (count !== undefined &&
        Number.isInteger(count) &&
        count > 0 &&
        count <= this.exercisable &&
        this.shareType !== undefined)
    );
  }

  validate(): boolean {
    if (this.isSelected) {
      this.count.validate();
      this.isShareTypeMissing = this.shareType === undefined;
    }
    return this.isValid;
  }

  toData(isEmployee: boolean): ProgramExercise | undefined {
    const { shareholderId } = this.candidate;
    const count = this.count.parsedValue;
    if (!this.isSelected || count === undefined || shareholderId === undefined || !this.shareType) {
      return undefined;
    }
    const { shareTypeId, shareTypeCode } = this.shareType;
    return {
      grantId: this.candidate.grantId,
      trancheId: this.candidate.trancheId,
      holderId: this.candidate.holderId,
      holderName: this.candidate.holderName,
      shareholderId,
      shareTypeId,
      shareTypeCode,
      strikePrice: this.candidate.strikePrice,
      count,
      isEmployee: isEmployee && !this.candidate.isCompany,
      receiver: this.receiver,
    };
  }
}

export class ExerciseProgramModel {
  readonly planId: string;
  readonly programName: string;
  readonly rows: ExerciseRowModel[];

  constructor(
    planId: string,
    programName: string,
    candidates: ExerciseCandidate[],
    exerciseDate: () => string | undefined,
    defaultShareType: ShareType | undefined,
    saved?: ExerciseProgram,
  ) {
    this.planId = planId;
    this.programName = programName;
    const savedByTranche = new Map(saved?.exercises.map((e) => [e.trancheId, e]));
    const programCandidates = candidates.filter((c) => c.planId === planId);
    const candidateTranches = new Set(programCandidates.map((c) => c.trancheId));
    const savedOnly = saved
      ? saved.exercises
          .filter((e) => !candidateTranches.has(e.trancheId))
          .map((e) => new ExerciseRowModel(savedCandidate(saved, e), e, false, exerciseDate))
      : [];
    this.rows = [
      ...programCandidates.map(
        (c) =>
          new ExerciseRowModel(
            c,
            savedByTranche.get(c.trancheId),
            saved === undefined,
            exerciseDate,
            defaultShareType,
          ),
      ),
      ...savedOnly,
    ];
    makeAutoObservable(this, { planId: false, programName: false }, { autoBind: true });
  }

  toData(isEmployee: (holderId: string) => boolean): ExerciseProgram {
    return {
      planId: this.planId,
      programName: this.programName,
      exercises: this.rows.flatMap((row) => row.toData(isEmployee(row.candidate.holderId)) ?? []),
    };
  }
}

export class OptionExerciseForm {
  readonly type = DecisionType.optionExercise;
  readonly context: DecisionContext<OptionExercise>;
  readonly dispatch: DbotEventDispatcher;
  readonly capitalIncreaseDecisionId?: string;
  programs: ExerciseProgramModel[];
  employeeHolderIds: string[];
  holdingCompanies: Record<string, HolderHoldingCompanies["holdingCompanies"]> = {};
  readonly exerciseDate: LocalDateFieldModel;
  error?: TMessage = undefined;

  constructor(
    dispatch: DbotEventDispatcher,
    context: DecisionContext<OptionExercise>,
    data?: OptionExercise,
  ) {
    this.dispatch = dispatch;
    this.context = context;
    this.capitalIncreaseDecisionId = data?.capitalIncreaseDecisionId;
    this.employeeHolderIds = uniq(
      (data?.programs ?? [])
        .flatMap((program) => program.exercises)
        .filter((exercise) => exercise.isEmployee)
        .map((exercise) => exercise.holderId),
    );
    const todayIso = toLocalDateISOString(today());
    this.exerciseDate = new LocalDateFieldModel({
      required: true,
      value: data?.exerciseDate ?? todayIso,
      maxDate: todayIso,
    });
    const candidates = candidatesIn(context);
    const defaultShareType = onlyShareType(shareTypesIn(context));
    this.programs = (data?.programs ?? []).map(
      (program) =>
        new ExerciseProgramModel(
          program.planId,
          program.programName,
          candidates,
          () => this.selectedExerciseDate(),
          defaultShareType,
          program,
        ),
    );
    makeAutoObservable(
      this,
      { type: false, context: false, dispatch: false, capitalIncreaseDecisionId: false },
      { autoBind: true },
    );
  }

  get candidates(): ExerciseCandidate[] {
    return candidatesIn(this.context);
  }

  selectedExerciseDate(): string | undefined {
    return this.exerciseDate.validatedValue;
  }

  get shareTypes(): ShareType[] {
    return shareTypesIn(this.context);
  }

  get isProjectLocked(): boolean {
    return this.context.isLocked;
  }

  get availablePrograms(): ExerciseProgramChoice[] {
    return exerciseProgramChoices(this.candidates).filter(
      (choice) => !this.programs.some((program) => program.planId === choice.planId),
    );
  }

  get capitalIncreaseDecision(): DecisionItem | undefined {
    return this.context.currentDecisions.find(
      (decision) => decision.decisionId === this.capitalIncreaseDecisionId,
    );
  }

  addProgram(planId: string) {
    const choice = this.availablePrograms.find((c) => c.planId === planId);
    if (choice === undefined) {
      return;
    }
    this.programs.push(
      new ExerciseProgramModel(
        planId,
        choice.programName,
        this.candidates,
        () => this.selectedExerciseDate(),
        onlyShareType(this.shareTypes),
      ),
    );
    this.loadHoldingCompanies(planId);
    this.error = undefined;
  }

  loadHoldingCompanies(planId: string) {
    this.dispatch(
      instrumentHolderApiClient.getHoldingCompanies(
        this.context.orgNo,
        { planId },
        {
          onSuccess: (res) => [thunk(() => this.setHoldingCompanies(res.data))],
          onFailure: () => [],
        },
      ),
    );
  }

  setHoldingCompanies(holders: HolderHoldingCompanies[]) {
    this.holdingCompanies = {
      ...this.holdingCompanies,
      ...Object.fromEntries(holders.map((holder) => [holder.holderId, holder.holdingCompanies])),
    };
  }

  receiverChoices(row: ExerciseRowModel): ReceiverChoice[] {
    const companies = (this.holdingCompanies[row.candidate.holderId] ?? []).map(
      (company): HoldingCompanyReceiver => ({
        organisationNumber: company.organisationNumber,
        name: company.name,
        shareholderId: company.shareholderId,
        contactEmail: row.candidate.holderEmail,
      }),
    );
    const saved =
      row.receiver &&
      !companies.some((company) => company.organisationNumber === row.receiver?.organisationNumber)
        ? [row.receiver]
        : [];
    return [
      { name: row.candidate.holderName },
      ...[...companies, ...saved].map((receiver) => ({
        name: receiver.name,
        organisationNumber: receiver.organisationNumber,
        receiver,
      })),
    ];
  }

  isEmployee(holderId: string): boolean {
    return this.employeeHolderIds.includes(holderId);
  }

  setEmployee(holderId: string, isEmployee: boolean) {
    this.employeeHolderIds = isEmployee
      ? uniq([...this.employeeHolderIds, holderId])
      : this.employeeHolderIds.filter((id) => id !== holderId);
  }

  removeProgram(planId: string) {
    this.programs = this.programs.filter((program) => program.planId !== planId);
  }

  openCapitalIncrease() {
    if (this.capitalIncreaseDecisionId !== undefined) {
      this.dispatch({
        type: "dbot/open-details-panel",
        panel: { type: "decision", decisionId: this.capitalIncreaseDecisionId },
      });
    }
  }

  get hasSelection(): boolean {
    return this.programs.some((program) => program.rows.some((row) => row.isSelected));
  }

  get isValid(): boolean {
    return (
      this.exerciseDate.validatedValue !== undefined &&
      this.exerciseDate.error === undefined &&
      this.hasSelection &&
      this.programs.every((program) => program.rows.every((row) => row.isValid))
    );
  }

  validate(): boolean {
    this.exerciseDate.validate();
    this.programs.forEach((program) => program.rows.forEach((row) => row.validate()));
    this.error = this.hasSelection ? undefined : { key: "decisions.optionExercise.no-exercises" };
    return this.isValid;
  }

  toData(): OptionExercise {
    return {
      type: DecisionType.optionExercise,
      programs: this.programs.map((program) => program.toData(this.isEmployee)),
      capitalIncreaseDecisionId: this.capitalIncreaseDecisionId,
      exerciseDate: this.exerciseDate.validatedValue,
    };
  }
}
